import { useState, useCallback, useEffect } from 'react';
import { getHiddenColumns, saveHiddenColumns } from 'api/columnVisibility';

const ANIMATION_DURATION = 150;

export const useColumnVisibility = (
  initialColumns: Record<string, boolean>,
) => {
  const [visibleColumns, setVisibleColumns] = useState(initialColumns);
  const [hidingColumn, setHidingColumn] = useState<string | null>(null);
  const [hidingColumns, setHidingColumns] = useState<Set<string>>(new Set());
  const [showingColumns, setShowingColumns] = useState<Set<string>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    getHiddenColumns()
      .then((hiddenKeys) => {
        setVisibleColumns((prev) => {
          const updated = { ...prev };
          hiddenKeys.forEach((key) => {
            updated[key] = false;
          });
          return updated;
        });
      })
      .catch((error) => console.error(error));
  }, []);

  const saveColumnsToBackend = useCallback(
    (columns: Record<string, boolean>) => {
      const hiddenKeys = Object.keys(columns).filter((key) => !columns[key]);
      saveHiddenColumns(hiddenKeys).catch((error) => console.error(error));
    },
    [],
  );

  const handleHideColumn = useCallback(
    (columnKey: string) => {
      setHidingColumn(columnKey);
      setTimeout(() => {
        setVisibleColumns((prev) => {
          const updated = { ...prev, [columnKey]: false };
          saveColumnsToBackend(updated);
          return updated;
        });
        setHidingColumn(null);
      }, ANIMATION_DURATION);
    },
    [saveColumnsToBackend],
  );

  const handleApplyColumnVisibility = useCallback(
    (newVisibleColumns: Record<string, boolean>) => {
      const currentColumns = visibleColumns;
      const columnsToHide = Object.keys(currentColumns).filter(
        (key) => currentColumns[key] && !newVisibleColumns[key],
      );
      const columnsToShow = Object.keys(newVisibleColumns).filter(
        (key) => !currentColumns[key] && newVisibleColumns[key],
      );

      const hasColumnsToHide = columnsToHide.length > 0;
      const hasColumnsToShow = columnsToShow.length > 0;

      if (hasColumnsToHide) {
        setHidingColumns(new Set(columnsToHide));
        setTimeout(() => {
          setVisibleColumns((prev) => {
            const updated = { ...prev };
            columnsToHide.forEach((key) => {
              updated[key] = false;
            });
            return updated;
          });
          setHidingColumns(new Set());
        }, ANIMATION_DURATION);
      }

      if (hasColumnsToShow) {
        const hideDelay = hasColumnsToHide ? ANIMATION_DURATION : 0;
        setTimeout(() => {
          setVisibleColumns((prev) => {
            const updated = { ...prev };
            columnsToShow.forEach((key) => {
              updated[key] = true;
            });
            return updated;
          });
          setShowingColumns(new Set(columnsToShow));

          setTimeout(() => {
            setShowingColumns(new Set());
          }, ANIMATION_DURATION);
        }, hideDelay);
      }

      if (!hasColumnsToHide && !hasColumnsToShow) {
        setVisibleColumns(newVisibleColumns);
      }

      const totalDelay =
        hasColumnsToHide && hasColumnsToShow
          ? ANIMATION_DURATION * 2
          : ANIMATION_DURATION;
      setTimeout(() => {
        saveColumnsToBackend(newVisibleColumns);
      }, totalDelay);
    },
    [visibleColumns, saveColumnsToBackend],
  );

  return {
    visibleColumns,
    hidingColumn,
    hidingColumns,
    showingColumns,
    dialogOpen,
    setDialogOpen,
    handleHideColumn,
    handleApplyColumnVisibility,
  };
};
